import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Download, Mail, Check, X, RefreshCw, Receipt, Trash2, Calendar, Plus, Pencil,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const CURRENCIES = ['ZAR', 'USD', 'GBP', 'EUR', 'AUD', 'CAD'];

export default function Invoicing({ onLogout }) {
  const navigate = useNavigate();
  const { childId: paramChildId } = useParams();

  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(paramChildId || '');
  const [rate, setRate] = useState('');
  const [savedRate, setSavedRate] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [currency, setCurrency] = useState('ZAR');
  const [parentEmail, setParentEmail] = useState('');
  const [notes, setNotes] = useState('Thank you for choosing PlayFirst Therapy.');
  const [taxRate, setTaxRate] = useState(0);

  // Optional client billing details — added at invoice-time (not stored
  // on the child) so each invoice carries its own snapshot for audit.
  // POPIA-friendly: all optional; therapist chooses what her client has
  // consented to and what her tax authority / medical scheme requires.
  const [clientDob, setClientDob] = useState('');
  const [clientIdNumber, setClientIdNumber] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientPhone, setClientPhone] = useState('');

  // South African medical-scheme claim fields (Feb 2026). All optional;
  // shown in a dedicated "Medical aid" block on the PDF only when at
  // least one is filled.
  const [medicalAidName, setMedicalAidName] = useState('');
  const [medicalAidNumber, setMedicalAidNumber] = useState('');
  const [mainMemberName, setMainMemberName] = useState('');

  // Per-group rate overrides — keyed by "{code}|{duration}" so a
  // therapist can charge different amounts per procedure code without
  // leaving this form. Populated after Preview so the therapist can
  // tweak each grouped line before generating.
  const [rateOverrides, setRateOverrides] = useState({}); // { "92507|30": 550, ... }

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [invoices, setInvoices] = useState([]);

  // ---- Manual mode state ----
  const [mode, setMode] = useState('soap'); // 'soap' | 'manual'
  const [manualBillTo, setManualBillTo] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualItems, setManualItems] = useState([
    {
      date: new Date().toISOString().slice(0, 10),
      description: 'Speech therapy session',
      quantity: 1,
      rate: '',
    },
  ]);

  // ---- Treatment code label editor state ----
  // Merged view: `codeLabels.common` is the built-in library; `.custom`
  // is what THIS therapist has overridden or added. We show them side
  // by side so she can (a) glance the common ones without cluttering
  // her own edits, (b) add / rename any she uses.
  const [codeLabels, setCodeLabels] = useState({ common: {}, custom: {}, merged: {} });
  const [codeLabelsOpen, setCodeLabelsOpen] = useState(false);
  const [newLabelCode, setNewLabelCode] = useState('');
  const [newLabelText, setNewLabelText] = useState('');
  const [savingCodeLabels, setSavingCodeLabels] = useState(false);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/children`, { headers: auth() });
        const arr = Array.isArray(r.data) ? r.data : (r.data?.children || []);
        setChildren(arr);
        if (!selectedChild && paramChildId) setSelectedChild(paramChildId);
      } catch {
        toast.error('Could not load children');
      }
      try {
        const r = await axios.get(`${API}/invoices/treatment-code-labels`, { headers: auth() });
        setCodeLabels(r.data || { common: {}, custom: {}, merged: {} });
      } catch {
        // Silent — the invoice can still be generated without the map.
      }
    };
    load();
    refreshInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Treatment code label edit helpers ----
  const saveCodeLabels = async (nextCustom) => {
    setSavingCodeLabels(true);
    try {
      const r = await axios.post(
        `${API}/invoices/treatment-code-labels`,
        { labels: nextCustom },
        { headers: auth() },
      );
      const custom = r.data?.custom || nextCustom;
      setCodeLabels((prev) => ({
        common: prev.common,
        custom,
        merged: { ...prev.common, ...custom },
      }));
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save labels');
    } finally {
      setSavingCodeLabels(false);
    }
  };

  const addCustomLabel = () => {
    const code = newLabelCode.trim();
    const label = newLabelText.trim();
    if (!code || !label) {
      toast.error('Type a code and a description');
      return;
    }
    const next = { ...codeLabels.custom, [code]: label };
    saveCodeLabels(next);
    setNewLabelCode('');
    setNewLabelText('');
  };

  const updateCustomLabel = (code, label) => {
    const next = { ...codeLabels.custom, [code]: label };
    setCodeLabels((prev) => ({
      ...prev,
      custom: next,
      merged: { ...prev.common, ...next },
    }));
  };

  const removeCustomLabel = (code) => {
    const next = { ...codeLabels.custom };
    delete next[code];
    saveCodeLabels(next);
  };

  useEffect(() => {
    if (!selectedChild) {
      setSavedRate(null);
      setRate('');
      // Still load all-therapist invoices so the list shows even before a child is picked.
      refreshInvoices();
      return;
    }
    const child = children.find((c) => c.id === selectedChild);
    if (child) {
      setSavedRate(child.session_rate || null);
      setRate(child.session_rate ? String(child.session_rate) : '');
    }
    refreshInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChild, children]);

  const refreshInvoices = async () => {
    try {
      const url = selectedChild
        ? `${API}/invoices/list/${selectedChild}`
        : `${API}/invoices/list`;
      const r = await axios.get(url, { headers: auth() });
      setInvoices(r.data?.invoices || []);
    } catch {}
  };

  const handlePreview = async () => {
    if (!selectedChild) { toast.error('Pick a child first'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ child_id: selectedChild, date_from: dateFrom, date_to: dateTo, currency });
      if (rate) params.set('rate', rate);
      const r = await axios.get(`${API}/invoices/preview?${params}`, { headers: auth() });
      setPreview(r.data);
      // Seed per-group rate overrides from the preview so the therapist
      // can edit each grouped line's rate before generating. Keyed by
      // "{code}|{duration}" — matches the backend's lookup keys exactly.
      const seed = {};
      for (const it of r.data.items || []) {
        const k = `${it.treatment_code || ''}|${it.duration_min || ''}`;
        seed[k] = it.rate;
      }
      setRateOverrides(seed);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRate = async () => {
    if (!selectedChild || !rate) return;
    try {
      await axios.post(
        `${API}/invoices/set-rate/${selectedChild}`,
        { rate: parseFloat(rate) },
        { headers: auth() },
      );
      setSavedRate(parseFloat(rate));
      toast.success('Rate saved');
    } catch {
      toast.error('Could not save rate');
    }
  };

  const handleGenerate = async () => {
    if (!preview || !preview.note_count) {
      toast.error('No SOAP notes in this date range. Run Preview first.');
      return;
    }
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/invoices/generate`,
        {
          child_id: selectedChild,
          date_from: dateFrom,
          date_to: dateTo,
          rate: rate ? parseFloat(rate) : undefined,
          currency,
          parent_email: parentEmail || undefined,
          notes,
          tax_rate: parseFloat(taxRate) || 0,
          // Optional client-billing snapshot (see backend
          // GenerateInvoiceRequest). Empty strings become undefined so
          // the payload stays compact for therapists who leave them blank.
          client_dob: clientDob.trim() || undefined,
          client_id_number: clientIdNumber.trim() || undefined,
          client_address: clientAddress.trim() || undefined,
          client_phone: clientPhone.trim() || undefined,
          // South African medical-aid claim fields — optional.
          medical_aid_name: medicalAidName.trim() || undefined,
          medical_aid_number: medicalAidNumber.trim() || undefined,
          main_member_name: mainMemberName.trim() || undefined,
          // Per-group rate overrides so the therapist can bill each
          // treatment code at its own rate.
          rate_overrides: Object.keys(rateOverrides).length ? rateOverrides : undefined,
        },
        { headers: auth() },
      );
      toast.success(`Invoice ${r.data.invoice_number} created`);
      setPreview(null);
      refreshInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generate failed');
    } finally {
      setGenerating(false);
    }
  };

  // ---- Manual invoice helpers ----
  const updateManualItem = (idx, key, value) => {
    setManualItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, [key]: value } : it)),
    );
  };
  const addManualItem = () => {
    setManualItems((prev) => [
      ...prev,
      {
        date: new Date().toISOString().slice(0, 10),
        description: '',
        quantity: 1,
        rate: '',
      },
    ]);
  };
  const removeManualItem = (idx) => {
    setManualItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const manualSubtotal = manualItems.reduce((sum, it) => {
    const q = parseFloat(it.quantity) || 0;
    const r = parseFloat(it.rate) || 0;
    return sum + q * r;
  }, 0);

  const handleGenerateManual = async () => {
    const billTo = manualBillTo.trim() || (selectedChild
      ? children.find((c) => c.id === selectedChild)?.name
      : '');
    if (!selectedChild && !billTo) {
      toast.error('Pick a child or enter a "Bill to" name.');
      return;
    }
    const validItems = manualItems
      .filter((it) => it.description?.trim())
      .map((it) => {
        const q = parseFloat(it.quantity) || 1;
        const r = parseFloat(it.rate) || 0;
        return {
          date: it.date || new Date().toISOString().slice(0, 10),
          description: it.description.trim(),
          quantity: q,
          rate: r,
          amount: Math.round(q * r * 100) / 100,
        };
      });
    if (!validItems.length) {
      toast.error('Add at least one line item with a description.');
      return;
    }

    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/invoices/generate-manual`,
        {
          child_id: selectedChild || undefined,
          bill_to_name: billTo || undefined,
          bill_to_email: manualEmail || parentEmail || undefined,
          items: validItems,
          currency,
          notes,
          tax_rate: parseFloat(taxRate) || 0,
          period_from: dateFrom || undefined,
          period_to: dateTo || undefined,
          // Same optional client-billing snapshot as the SOAP flow.
          client_dob: clientDob.trim() || undefined,
          client_id_number: clientIdNumber.trim() || undefined,
          client_address: clientAddress.trim() || undefined,
          client_phone: clientPhone.trim() || undefined,
          medical_aid_name: medicalAidName.trim() || undefined,
          medical_aid_number: medicalAidNumber.trim() || undefined,
          main_member_name: mainMemberName.trim() || undefined,
        },
        { headers: auth() },
      );
      toast.success(`Invoice ${r.data.invoice_number} created`);
      setManualItems([
        {
          date: new Date().toISOString().slice(0, 10),
          description: 'Speech therapy session',
          quantity: 1,
          rate: '',
        },
      ]);
      setManualBillTo('');
      setManualEmail('');
      refreshInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generate failed');
    } finally {
      setGenerating(false);
    }
  };

  const downloadPdf = async (inv) => {
    try {
      const r = await axios.get(`${API}/invoices/${inv.id}/pdf`, {
        headers: auth(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download PDF');
    }
  };

  const sendEmail = async (inv) => {
    const to = window.prompt('Send invoice to email:', inv.parent_email || parentEmail || '');
    if (!to) return;
    try {
      await axios.post(`${API}/invoices/${inv.id}/email`, { invoice_id: inv.id, parent_email: to }, { headers: auth() });
      toast.success(`Queued for ${to} — arrives in a few seconds`);
      // Give the background task a moment, then refresh so the "sent" badge appears.
      setTimeout(() => refreshInvoices(), 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    }
  };

  const togglePaid = async (inv) => {
    const path = inv.status === 'paid' ? 'mark-unpaid' : 'mark-paid';
    try {
      await axios.post(`${API}/invoices/${inv.id}/${path}`, {}, { headers: auth() });
      refreshInvoices();
    } catch {
      toast.error('Update failed');
    }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete ${inv.invoice_number}?`)) return;
    try {
      await axios.delete(`${API}/invoices/${inv.id}`, { headers: auth() });
      refreshInvoices();
    } catch {
      toast.error('Delete failed');
    }
  };

  const fmt = (n, cur) => `${cur || 'ZAR'} ${Number(n || 0).toFixed(2)}`;
  const statusColor = (s) =>
    s === 'paid' ? 'bg-emerald-100 text-emerald-800'
    : s === 'sent' ? 'bg-blue-100 text-blue-800'
    : 'bg-stone-100 text-stone-700';

  return (
    <div className="min-h-screen bg-stone-50" data-testid="invoicing-page">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="invoicing-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div>
            <div className="text-xs font-semibold tracking-wide text-purple-700">PLAYFIRST · INVOICING</div>
            <h1 className="text-xl font-bold text-stone-900 flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              {mode === 'manual' ? 'Generate invoice manually' : 'Generate invoice from SOAP notes'}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Mode toggle */}
        <div className="flex gap-2" data-testid="invoicing-mode-toggle">
          <Button
            variant={mode === 'soap' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('soap')}
            data-testid="invoicing-mode-soap"
          >
            <FileText className="w-4 h-4 mr-2" />
            From SOAP notes
          </Button>
          <Button
            variant={mode === 'manual' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('manual')}
            data-testid="invoicing-mode-manual"
          >
            <Pencil className="w-4 h-4 mr-2" />
            Manual entry
          </Button>
          <span className="text-xs text-stone-500 self-center ml-2">
            {mode === 'soap'
              ? 'Auto-pulls finalized SOAP notes for the date range.'
              : 'Type each line item yourself — no SOAP needed.'}
          </span>
        </div>

        <Card className="p-5">
          <h2 className="text-sm font-semibold text-stone-900 mb-4">1. Set up</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-sm">Child</Label>
              <Select value={selectedChild} onValueChange={setSelectedChild}>
                <SelectTrigger data-testid="invoicing-child-select"><SelectValue placeholder="Pick a child" /></SelectTrigger>
                <SelectContent>
                  {children.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`invoicing-child-${c.id}`}>
                      {c.name}{c.session_rate ? ` · rate ${c.session_rate}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> From
              </Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="invoicing-date-from" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> To
              </Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="invoicing-date-to" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Rate per session</Label>
              <div className="flex gap-2">
                <Input
                  type="number" step="0.01" placeholder="e.g. 750" value={rate}
                  onChange={(e) => setRate(e.target.value)} data-testid="invoicing-rate"
                />
                {rate && parseFloat(rate) !== savedRate && (
                  <Button type="button" variant="outline" size="sm" onClick={handleSaveRate} data-testid="invoicing-save-rate">
                    Save
                  </Button>
                )}
              </div>
              <p className="text-xs text-stone-500">{savedRate ? `Saved on profile: ${savedRate}` : 'Set once, reuse on every invoice'}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="invoicing-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Parent email (optional)</Label>
              <Input type="email" placeholder="parent@example.com" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} data-testid="invoicing-parent-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Tax rate % (optional)</Label>
              <Input type="number" step="0.01" min="0" max="100" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="invoicing-tax-rate" />
            </div>

            {/* Optional client billing details — SA therapists often need
                ID + address for medical scheme claims. All optional so a
                therapist only fills what her client has consented to.
                See routes/invoicing.py POPIA docstring for the compliance
                reasoning. */}
            <div className="sm:col-span-2 pt-4 mt-2 border-t border-stone-200">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-stone-900">Client billing details (optional)</h3>
              </div>
              <p className="text-xs text-stone-500 mb-3">
                Only include what your client has consented to. Used for this invoice + your own records.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm">Client date of birth</Label>
                  <Input
                    type="date"
                    value={clientDob}
                    onChange={(e) => setClientDob(e.target.value)}
                    data-testid="invoicing-client-dob"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Client ID number</Label>
                  <Input
                    type="text"
                    placeholder="e.g. 9001015800081"
                    value={clientIdNumber}
                    onChange={(e) => setClientIdNumber(e.target.value)}
                    data-testid="invoicing-client-id"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Client contact number</Label>
                  <Input
                    type="tel"
                    placeholder="+27 82 123 4567"
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    data-testid="invoicing-client-phone"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Client address</Label>
                  <Textarea
                    rows={2}
                    placeholder="Street, Suburb, City, Postal code"
                    value={clientAddress}
                    onChange={(e) => setClientAddress(e.target.value)}
                    className="resize-none"
                    data-testid="invoicing-client-address"
                  />
                </div>
              </div>
            </div>

            {/* South African medical-aid claim block. All optional and
                only rendered on the PDF when at least one field is
                filled — so therapists who never submit to a scheme
                keep the same compact invoice they had before. */}
            <div className="sm:col-span-2 pt-4 mt-2 border-t border-stone-200">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-stone-900">Medical aid (optional)</h3>
              </div>
              <p className="text-xs text-stone-500 mb-3">
                Only fill these if you&apos;re submitting this invoice to a South African medical scheme.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm">Medical aid / scheme name</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Discovery Health"
                    value={medicalAidName}
                    onChange={(e) => setMedicalAidName(e.target.value)}
                    data-testid="invoicing-medical-aid-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Medical aid number</Label>
                  <Input
                    type="text"
                    placeholder="Member number"
                    value={medicalAidNumber}
                    onChange={(e) => setMedicalAidNumber(e.target.value)}
                    data-testid="invoicing-medical-aid-number"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-sm">Main member name</Label>
                  <Input
                    type="text"
                    placeholder="Full name of the main member on the medical aid"
                    value={mainMemberName}
                    onChange={(e) => setMainMemberName(e.target.value)}
                    data-testid="invoicing-main-member-name"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-sm">Notes for the invoice (optional)</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="resize-none" data-testid="invoicing-notes" />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            {mode === 'soap' && (
              <Button onClick={handlePreview} disabled={loading || !selectedChild} variant="outline" data-testid="invoicing-preview-btn">
                {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Preview from SOAP notes
              </Button>
            )}
          </div>
        </Card>

        {/* Treatment code label editor — collapsible. Descriptions
            here appear in the invoice line item (e.g. "12 × 30-min
            Speech-language therapy (Code 92507)"). Common codes are
            read-only reference; custom codes are the therapist's own
            overrides / additions. */}
        <Card className="p-5" data-testid="invoicing-code-labels-card">
          <button
            type="button"
            onClick={() => setCodeLabelsOpen((v) => !v)}
            className="w-full flex items-center justify-between text-left"
            data-testid="invoicing-code-labels-toggle"
          >
            <div>
              <h2 className="text-sm font-semibold text-stone-900">Treatment code labels</h2>
              <p className="text-xs text-stone-500 mt-0.5">
                {Object.keys(codeLabels.custom || {}).length} custom · {Object.keys(codeLabels.common || {}).length} built-in
                {' · '}shown on invoice lines next to the code
              </p>
            </div>
            <span className="text-stone-500 text-lg">{codeLabelsOpen ? '−' : '+'}</span>
          </button>

          {codeLabelsOpen && (
            <div className="mt-4 space-y-5">
              {/* Custom labels editor */}
              <div>
                <h3 className="text-xs font-semibold text-stone-900 mb-2">Your codes</h3>
                {Object.keys(codeLabels.custom || {}).length === 0 && (
                  <p className="text-xs text-stone-500 py-2">
                    No custom codes yet. Add one below — useful if you use a scheme-specific tariff (e.g. BHF 0117) or an internal code.
                  </p>
                )}
                <div className="space-y-2">
                  {Object.entries(codeLabels.custom || {}).map(([code, label]) => (
                    <div key={code} className="grid grid-cols-12 gap-2 items-center" data-testid={`invoicing-custom-code-row-${code}`}>
                      <Input
                        value={code}
                        disabled
                        className="col-span-3 font-mono text-xs bg-stone-50"
                      />
                      <Input
                        value={label}
                        onChange={(e) => updateCustomLabel(code, e.target.value)}
                        onBlur={() => saveCodeLabels(codeLabels.custom)}
                        className="col-span-8 text-sm"
                        data-testid={`invoicing-custom-code-label-${code}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCustomLabel(code)}
                        className="col-span-1 text-red-600"
                        disabled={savingCodeLabels}
                        data-testid={`invoicing-custom-code-remove-${code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-12 gap-2 items-center mt-3 pt-3 border-t border-stone-100">
                  <Input
                    placeholder="Code"
                    value={newLabelCode}
                    onChange={(e) => setNewLabelCode(e.target.value)}
                    className="col-span-3 font-mono text-xs"
                    data-testid="invoicing-code-label-new-code"
                  />
                  <Input
                    placeholder="Description (e.g. Speech-language therapy)"
                    value={newLabelText}
                    onChange={(e) => setNewLabelText(e.target.value)}
                    className="col-span-7 text-sm"
                    data-testid="invoicing-code-label-new-text"
                    onKeyDown={(e) => { if (e.key === 'Enter') addCustomLabel(); }}
                  />
                  <Button
                    size="sm"
                    onClick={addCustomLabel}
                    disabled={savingCodeLabels || !newLabelCode || !newLabelText}
                    className="col-span-2"
                    data-testid="invoicing-code-label-add"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
              </div>

              {/* Common codes (read-only reference) */}
              <div>
                <h3 className="text-xs font-semibold text-stone-900 mb-2">Common codes (built in)</h3>
                <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-stone-600">
                  {Object.entries(codeLabels.common || {}).map(([code, label]) => (
                    <div key={code} className="flex items-baseline gap-2">
                      <span className="font-mono text-stone-500 min-w-[3.5rem]">{code}</span>
                      <span className="truncate">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Card>

        {mode === 'manual' && (
          <Card className="p-5" data-testid="invoicing-manual-entry">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-stone-900">2. Manual line items</h2>
              <span className="text-lg font-bold text-stone-900">{fmt(manualSubtotal, currency)}</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Bill to (name on invoice)</Label>
                <Input
                  placeholder={selectedChild ? children.find((c) => c.id === selectedChild)?.name || '' : 'e.g. Jane Doe'}
                  value={manualBillTo}
                  onChange={(e) => setManualBillTo(e.target.value)}
                  data-testid="invoicing-manual-bill-to"
                />
                <p className="text-xs text-stone-500">
                  {selectedChild
                    ? `Defaults to selected child: ${children.find((c) => c.id === selectedChild)?.name || ''}`
                    : 'Leave a child unselected to bill someone outside your caseload.'}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Recipient email (optional)</Label>
                <Input
                  type="email"
                  placeholder="parent@example.com"
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  data-testid="invoicing-manual-email"
                />
              </div>
            </div>

            <div className="space-y-2 mb-4">
              {manualItems.map((it, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 items-end"
                  data-testid={`invoicing-manual-item-${idx}`}
                >
                  <div className="col-span-12 sm:col-span-3">
                    <Label className="text-xs text-stone-500">Date</Label>
                    <Input
                      type="date"
                      value={it.date}
                      onChange={(e) => updateManualItem(idx, 'date', e.target.value)}
                      data-testid={`manual-item-date-${idx}`}
                    />
                  </div>
                  <div className="col-span-12 sm:col-span-5">
                    <Label className="text-xs text-stone-500">Description</Label>
                    <Input
                      placeholder="e.g. Speech therapy session"
                      value={it.description}
                      onChange={(e) => updateManualItem(idx, 'description', e.target.value)}
                      data-testid={`manual-item-desc-${idx}`}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-1">
                    <Label className="text-xs text-stone-500">Qty</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      value={it.quantity}
                      onChange={(e) => updateManualItem(idx, 'quantity', e.target.value)}
                      data-testid={`manual-item-qty-${idx}`}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <Label className="text-xs text-stone-500">Rate</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={it.rate}
                      onChange={(e) => updateManualItem(idx, 'rate', e.target.value)}
                      data-testid={`manual-item-rate-${idx}`}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex items-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeManualItem(idx)}
                      disabled={manualItems.length === 1}
                      className="text-red-600 w-full"
                      data-testid={`manual-item-remove-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={addManualItem}
                data-testid="invoicing-manual-add-item"
              >
                <Plus className="w-4 h-4 mr-1" /> Add line
              </Button>
              <Button
                onClick={handleGenerateManual}
                disabled={generating}
                data-testid="invoicing-manual-generate-btn"
              >
                {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
                Generate invoice
              </Button>
            </div>
          </Card>
        )}

        {mode === 'soap' && preview && (
          <Card className="p-5" data-testid="invoicing-preview">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-stone-900">2. Preview ({preview.note_count} session{preview.note_count !== 1 ? 's' : ''}, {preview.items.length} line{preview.items.length !== 1 ? 's' : ''})</h2>
              <span className="text-lg font-bold text-stone-900">{fmt(preview.subtotal, preview.currency)}</span>
            </div>
            {/* Codes auto-extracted from SOAP + child profile. Rendered
                on the PDF's BILLING CODES block. */}
            {(preview.treatment_codes_used?.length > 0 || preview.icd10_codes_used?.length > 0) && (
              <div className="mb-4 rounded-lg bg-purple-50 border border-purple-100 p-3 space-y-1" data-testid="invoicing-preview-codes">
                {preview.treatment_codes_used?.length > 0 && (
                  <p className="text-xs text-stone-700"><span className="font-semibold text-purple-800">Treatment codes:</span> {preview.treatment_codes_used.join(', ')}</p>
                )}
                {preview.icd10_codes_used?.length > 0 && (
                  <p className="text-xs text-stone-700"><span className="font-semibold text-purple-800">ICD-10:</span> {preview.icd10_codes_used.map((c) => c.label ? `${c.code} — ${c.label}` : c.code).join(', ')}</p>
                )}
                <p className="text-[11px] text-stone-500 pt-1">These will appear on the invoice PDF and can be submitted to a medical scheme.</p>
              </div>
            )}
            {preview.note_count === 0 ? (
              <p className="text-sm text-stone-500 py-4 text-center">No completed SOAP notes in this date range.</p>
            ) : (
              <div className="space-y-2 mb-4 text-sm">
                {preview.items.map((it, i) => {
                  const key = `${it.treatment_code || ''}|${it.duration_min || ''}`;
                  const currentRate = rateOverrides[key] ?? it.rate;
                  const lineAmount = (Number(currentRate) || 0) * (it.quantity || 1);
                  return (
                    <div key={i} className="py-2 border-b border-stone-100 last:border-0" data-testid={`invoicing-preview-item-${i}`}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-stone-700 flex-1">{it.description}</span>
                        <span className="font-medium text-stone-900">{fmt(lineAmount, preview.currency)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-stone-500">
                        <span>Rate per session:</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={currentRate}
                          onChange={(e) => {
                            const v = e.target.value;
                            setRateOverrides((prev) => ({ ...prev, [key]: v === '' ? 0 : parseFloat(v) }));
                          }}
                          className="h-7 w-24 text-xs"
                          data-testid={`invoicing-rate-override-${key.replace('|', '-')}`}
                        />
                        <span>× {it.quantity}</span>
                        {it.session_dates?.length > 0 && (
                          <span className="ml-auto text-[10px] text-stone-400">{it.session_dates.length} date{it.session_dates.length !== 1 ? 's' : ''}: {it.session_dates[0]}{it.session_dates.length > 1 ? ` … ${it.session_dates[it.session_dates.length - 1]}` : ''}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <Button onClick={handleGenerate} disabled={generating || !preview.note_count} data-testid="invoicing-generate-btn">
              {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
              Generate invoice
            </Button>
          </Card>
        )}

        {invoices.length > 0 && (
          <Card className="p-5" data-testid="invoicing-list">
            <h2 className="text-sm font-semibold text-stone-900 mb-4">Invoices ({invoices.length})</h2>
            <div className="divide-y divide-stone-100">
              {invoices.map((inv) => (
                <div key={inv.id} className="py-3 flex items-center gap-3" data-testid={`invoice-row-${inv.invoice_number}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-stone-900">{inv.invoice_number}</span>
                      <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${statusColor(inv.status)}`}>{inv.status}</span>
                    </div>
                    <div className="text-xs text-stone-500">
                      {inv.period_from} → {inv.period_to} · {inv.items?.length || 0} session{inv.items?.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <span className="font-bold text-stone-900">{fmt(inv.total, inv.currency)}</span>
                  <Button size="sm" variant="ghost" onClick={() => downloadPdf(inv)} title="Download PDF" data-testid={`invoice-pdf-${inv.invoice_number}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => sendEmail(inv)} title="Email parent" data-testid={`invoice-email-${inv.invoice_number}`}>
                    <Mail className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => togglePaid(inv)} title="Toggle paid" data-testid={`invoice-paid-${inv.invoice_number}`}>
                    {inv.status === 'paid' ? <X className="w-4 h-4 text-emerald-700" /> : <Check className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteInvoice(inv)} title="Delete" className="text-red-600" data-testid={`invoice-delete-${inv.invoice_number}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
